import PropTypes from 'prop-types';
import { useMemo, useCallback, memo } from 'react';

import { trans } from '@spotahome/soyuz/client';
import {
  CheckboxWithLabel,
  BaseChip,
  cityToggleConfig,
  CloseButton,
  FormGroupToggle
} from '@spotahome/ui-library';
import { useMediaQuery } from '@spotahome/ui-library/src/tenant/components/utils/breakpoints/breakpoints';

// lodash utils
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { useFiltersContext } from '@spotahome/ui-library/src/tenant/context/FiltersContext';
import { isFilteredByDate } from '@spotahome/ui-library/src/tenant/components/utils/filters';
import FilterHeader from '@spotahome/ui-library/src/tenant/components/Filters/FilterHeader';
import { DSIcon } from '@spotahome/ui-library/base';

import FilterGroup from '../FilterGroup';

import PreFilters, { mustShowPrefilters } from '../PreFilters';

import ClearFiltersButton from './ClearFiltersButton';
import CheckboxWithFacet from './CheckboxWithFacet';

import NUMBER_BATHROOMS_TOGGLE from './bathrooms-config';
import BED_TYPE_TOGGLE from './beds-config';
import { FEATURES } from './features-config';
import { RULES } from './rules-config';

import './Filters.scss';

const SHOW_FILTERED_PROPERTIES = 1;
const SHOW_ALL_PROPERTIES = 0;

const isValueChecked = (arr = [], value) => arr.includes(value);

const Filters = ({
  cityId,
  onFiltersUpdate,
  onClearFilters = () => {},
  onCloseFilters = () => {},
  activeFiltersCount = 0
}) => {
  const { filters } = useFiltersContext();
  const { isBiggerThanTablet } = useMediaQuery();

  const handleToggleCheckbox = useCallback(
    ({ name, value, isChecked }) => {
      let options = filters[name];
      if (isChecked) {
        options = [...options, value];
      } else {
        options = options.filter(option => option !== value);
      }

      SoyuzAnalytics.sendGA4Event('filter', {
        context: 'filters',
        section: name !== 'bathrooms[]' ? value : `${name}-${value}`,
        action: isChecked ? 'check' : 'uncheck'
      });

      onFiltersUpdate({
        [name]: uniq(options)
      });
    },
    [filters, onFiltersUpdate]
  );

  const handleSingleFilterUpdate = useCallback(
    (name, eventName) => isChecked => {
      SoyuzAnalytics.sendGA4Event('filter', {
        context: 'filters',
        section: eventName || name,
        action: isChecked ? 'check' : 'uncheck'
      });

      onFiltersUpdate({
        [name]: isChecked ? SHOW_FILTERED_PROPERTIES : SHOW_ALL_PROPERTIES
      });
    },
    [onFiltersUpdate]
  );

  const isSingleFilterChecked = useCallback(
    name => {
      const filter = parseInt(filters[name], 10);
      return filter === SHOW_FILTERED_PROPERTIES;
    },
    [filters]
  );

  const isFeatureChecked = useCallback(
    value => isValueChecked(filters['features[]'], value),
    [filters]
  );

  const handleBathroomClick = useCallback(
    (value, isChecked) =>
      handleToggleCheckbox({
        name: 'bathrooms[]',
        value,
        isChecked
      }),
    [handleToggleCheckbox]
  );

  const handleBedTypesClick = useCallback(
    (value, isChecked) => {
      SoyuzAnalytics.sendGA4Event('filter', {
        context: 'filters',
        section: `bed-${value}`
      });

      let selectedValues = [];
      if (!isEmpty(filters.bed)) {
        selectedValues = filters.bed.split(',');
      }

      if (isChecked) {
        selectedValues.push(value);
        selectedValues = uniq(selectedValues);
      } else {
        selectedValues = selectedValues.filter(
          currentVal => currentVal !== value
        );
      }

      onFiltersUpdate({ bed: selectedValues.join(',') });
    },
    [filters.bed, onFiltersUpdate]
  );

  const handleToggleFilterFeaturesCheckbox = useCallback(
    value => isChecked =>
      handleToggleCheckbox({
        name: 'features[]',
        value,
        isChecked
      }),
    [handleToggleCheckbox]
  );

  const handleClearFilters = useCallback(
    e => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      onClearFilters();
    },
    [onClearFilters]
  );

  const handleCloseFilters = useCallback(
    e => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      onCloseFilters(e);
    },
    [onCloseFilters]
  );

  const renderFeaturesGroup = useCallback(
    (group, title) => (
      <FilterGroup
        classModifier="checkboxes"
        label={trans(`search.filter.${title}`)}
      >
        {group.map(({ value, labelKey, cy }) => (
          <CheckboxWithFacet
            key={value}
            name={value}
            id={`${value}-id`}
            text={trans(labelKey)}
            callback={handleToggleFilterFeaturesCheckbox(value)}
            checked={isFeatureChecked(value)}
            dataAttributes={{ cy }}
          />
        ))}
      </FilterGroup>
    ),
    [handleToggleFilterFeaturesCheckbox, isFeatureChecked]
  );

  const renderPrefilters = useCallback(() => {
    const hidePrefilters = !mustShowPrefilters(filters);

    return hidePrefilters ? null : (
      <>
        <FilterGroup
          label={trans('search.filter.recommended')}
          classModifier="column"
        >
          <PreFilters onFiltersUpdate={onFiltersUpdate} />
        </FilterGroup>
      </>
    );
  }, [onFiltersUpdate, filters]);

  const showSoldOutFilter = useMemo(() => isFilteredByDate(filters), [filters]);

  const featuresGroup = useMemo(
    () => renderFeaturesGroup(FEATURES, 'features'),
    [renderFeaturesGroup]
  );

  const rulesGroup = useMemo(
    () => renderFeaturesGroup(RULES, 'rules'),
    [renderFeaturesGroup]
  );

  const preFiltersGroup = useMemo(() => renderPrefilters(), [renderPrefilters]);

  const cityConfig = useMemo(() => cityToggleConfig(cityId), [cityId]);

  const bathroomOptions = useMemo(
    () => NUMBER_BATHROOMS_TOGGLE(trans('search.filter.bathrooms.2-or-more')),
    []
  );

  const bedOptions = useMemo(() => BED_TYPE_TOGGLE(), []);

  return (
    <div data-test="expanded-full-filters">
      <div className="filters__header">
        <div className="filters__title">
          <FilterHeader title={trans('filters.title')} />
          <ClearFiltersButton
            activeFiltersCount={activeFiltersCount}
            onClearFilters={handleClearFilters}
          />
        </div>

        <CloseButton onClick={handleCloseFilters} />
      </div>

      {showSoldOutFilter && (
        <FilterGroup
          classModifier="checkboxes"
          label={trans('filters.label.availabilities')}
        >
          <CheckboxWithLabel
            id="soldout"
            name="soldout"
            text={trans('search.filter.soldout.text')}
            callback={handleSingleFilterUpdate(
              'includeBlockedProperties',
              'soldout'
            )}
            checked={isSingleFilterChecked('includeBlockedProperties')}
          />
        </FilterGroup>
      )}
      {preFiltersGroup}
      {featuresGroup}
      {rulesGroup}
      <FilterGroup
        classModifier="property-type-checkboxes filter-group--smaller"
        label={trans('search.filter.advanced-search')}
      >
        <CheckboxWithFacet
          id="verified"
          name="verified"
          labelTitle={
            <BaseChip
              isSpaced
              inline
              colorSchema="tag"
              size="small"
              label={trans(
                'search.filter.verified.vp.title'
              ).toLocaleUpperCase()}
              renderLeadingIcon={() => (
                <DSIcon name="check_circle_transparent" />
              )}
            />
          }
          text={
            isBiggerThanTablet() ? trans('search.filter.verified.text') : null
          }
          callback={handleSingleFilterUpdate('verified')}
          checked={isSingleFilterChecked('verified')}
        />

        {cityConfig.isNoDepositSupported && (
          <CheckboxWithFacet
            id="noDeposit"
            name="noDeposit"
            dataAttributes={{ cy: 'no-deposit-filter' }}
            labelTitle={
              <BaseChip
                isSpaced
                inline
                colorSchema="tag"
                size="small"
                label={trans(
                  'search.filter.no-deposit.vp.title'
                ).toLocaleUpperCase()}
                renderLeadingIcon={() => <DSIcon name="savings" />}
              />
            }
            text={
              isBiggerThanTablet()
                ? trans('search.filter.no-deposit.text')
                : null
            }
            callback={handleSingleFilterUpdate('noDeposit')}
            checked={isSingleFilterChecked('noDeposit')}
          />
        )}
      </FilterGroup>
      <FilterGroup
        classModifier="toggle filter-group--smaller"
        label={trans('search.filter.type_bed')}
      >
        <FormGroupToggle
          className="filters__group-toggle"
          onChange={handleBedTypesClick}
          selectedValues={filters.bed}
          options={bedOptions}
        />
      </FilterGroup>
      <FilterGroup
        classModifier="align-label-to-first-row filter-group--smaller"
        label={trans('search.filter.bathrooms')}
      >
        <FormGroupToggle
          className="filters__group-toggle"
          onChange={handleBathroomClick}
          selectedValues={filters['bathrooms[]'].join(',')}
          options={bathroomOptions}
        />
        <div>
          <CheckboxWithFacet
            name="ensuiteRoom"
            id="ensuiteRoom-id"
            dataAttributes={{ cy: 'bathroom-private-bathroom' }}
            text={trans('search.filter.ensuiteRoom')}
            callback={handleToggleFilterFeaturesCheckbox('ensuiteRoom')}
            checked={isFeatureChecked('ensuiteRoom')}
          />
        </div>
      </FilterGroup>
    </div>
  );
};

Filters.propTypes = {
  cityId: PropTypes.string.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func,
  activeFiltersCount: PropTypes.number,
  onCloseFilters: PropTypes.func
};

export default memo(Filters);
